<template>
  <div class="step-promo">
    <toggle-field
      :value="enabled"
      :active-value="true"
      :inactive-value="false"
      label="Include a promo code in each email"
      :hint="hintText"
      @input="$emit('update:enabled', $event)"
    />

    <template v-if="enabled">
      <info-hint v-if="!groups.length" variant="warning" icon="icon-tag" class="mb-0 mt-3">
        Create a promo code group with default settings first. Set the discount, the events it applies to and any eligibility rules such as a minimum party size there; the campaign issues each customer a single use code from those settings.
        <router-link to="/purchases/promos" class="text-white ml-1">Open promo codes</router-link>
      </info-hint>

      <div v-else class="row mt-3">
        <div class="col-md-4">
          <label>Promo code group</label>
          <base-input class="mb-0">
            <el-select
              :value="promoTemplateId"
              filterable
              placeholder="Choose a promo code group"
              class="select-primary w-100"
              @input="$emit('update:promo-template-id', $event)"
            >
              <el-option v-for="g in groups" :key="g.id" :value="g.id" :label="g.name" class="select-primary" />
            </el-select>
          </base-input>
          <p class="mt-1 mb-0 step-promo__muted"><small>Codes are built from this group's default settings and look like {{ promoPrefix }}-7K2M9Q.</small></p>
        </div>
        <div class="col-md-4 col-6">
          <label>Valid from</label>
          <div class="step-promo__pair">
            <base-input class="step-promo__pair-days mb-0">
              <el-input-number :value="daysBefore" :controls="false" :min="0" :max="365" :precision="0" placeholder="2" @input="$emit('update:days-before', $event)" />
            </base-input>
            <label class="m-0">days before the birthday</label>
          </div>
        </div>
        <div class="col-md-4 col-6">
          <label>Valid until</label>
          <div class="step-promo__pair">
            <base-input class="step-promo__pair-days mb-0">
              <el-input-number :value="daysAfter" :controls="false" :min="0" :max="365" :precision="0" placeholder="2" @input="$emit('update:days-after', $event)" />
            </base-input>
            <label class="m-0">days after the birthday</label>
          </div>
        </div>
      </div>

      <info-hint v-if="groups.length && !templateHasCode" variant="warning" class="mb-0 mt-3">
        The selected template does not contain {{ promoToken }}, so customers will not see their code.
      </info-hint>
    </template>
  </div>
</template>

<script setup lang="ts">
import { type PropType } from 'vue'
import ToggleField from '@/components/PaymentPages/ToggleField.vue'

defineProps({
  enabled: { type: Boolean, default: false },
  promoTemplateId: { type: Number as PropType<number | null>, default: null },
  daysBefore: { type: Number, default: 2 },
  daysAfter: { type: Number, default: 2 },
  // promo code groups with default settings (has_template = 1)
  groups: { type: Array as PropType<any[]>, default: () => [] },
  // prefix the issued codes carry, derived by the parent from the chosen group's name
  promoPrefix: { type: String, default: 'PROMO' },
  templateHasCode: { type: Boolean, default: false }
})

defineEmits(['update:enabled', 'update:promo-template-id', 'update:days-before', 'update:days-after'])

// Written as constants: Vue 2 does not allow {{ }} inside attribute values or
// text without treating them as interpolation
const promoToken = '{{promo.code}}'
const hintText = `Each email gets its own single use code built from a promo code group's default settings, valid only around that customer's birthday. Add ${promoToken} to the template to show it.`
</script>

<script lang="ts">
export default { name: 'campaign-step-promo' }
</script>

<style lang="scss" scoped>
// Layout only; controls, hints and labels are global primitives.
.step-promo {
  &__pair {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  &__pair-days {
    width: 90px;
    flex-shrink: 0;
  }

  &__muted {
    color: rgba(255, 255, 255, 0.75);
  }
}

.white-content .step-promo__muted {
  color: rgba(0, 0, 0, 0.7);
}
</style>
